import type { OwnedParking } from '@repo/domain';
import { ArrowRightIcon, CheckCircleIcon, CheckIcon } from '@repo/icons';
import { tr } from '@repo/locale';
import type { TestId } from '@repo/test-id';
import { useOwnedParkingActions } from '@repo/domain-service';
import { toast } from '@repo/toast';
import { useState } from 'react';
import { Button } from '@repo/ui';

import ActivationChecklist from '@features/ownedParkingManage/components/activation/ActivationChecklist';
import { getActivationReadiness } from '@features/ownedParkingManage/utils/getActivationReadiness';
import StatusInfo from '@features/ownedParkingManage/components/StatusInfo';

import { handleRepositoryError } from '@shared/utils/handleRepositoryError';

interface Props {
  parking: OwnedParking;
  testId: TestId;
}

export default function ManageParkingDraftContainer({ parking, testId }: Props) {
  const readiness = getActivationReadiness(parking);
  const [activating, setActivating] = useState<boolean>(false);
  const { submitForVerification } = useOwnedParkingActions();

  const handleActivate = async () => {
    setActivating(true);
    try {
      await submitForVerification({ id: parking.id });
      toast.success(tr('owned_parking_manage:manage.parking.action.activate.success'), testId.append('successToast'));
    } catch (error) {
      handleRepositoryError(error, {
        fallback: tr('owned_parking_manage:manage.parking.action.activate.failure'),
        testId,
      });
    } finally {
      setActivating(false);
    }
  };

  return (
    <>
      {readiness.state === 'cantRequest' && (
        <ActivationChecklist readiness={readiness} parkingId={parking.id} testId={testId.append('cantRequest')} />
      )}
      {readiness.state === 'notRequested' && (
        <StatusInfo
          className="bg-success-soft border-stroke-success-subtle text-fg-success-strong border"
          title={tr('owned_parking_manage:parking_statuses.draft.activation.not_requested.alert')}
          info={tr('owned_parking_manage:parking_statuses.draft.activation.not_requested.info')}
          icon={CheckCircleIcon}
          testId={testId.append('notRequested')}
        >
          <div className="my-1 flex justify-center">
            <Button
              color="success"
              label={tr('owned_parking_manage:parking_statuses.draft.activation.not_requested.button')}
              leftIcon={CheckIcon}
              rightIcon={ArrowRightIcon}
              onClick={handleActivate}
              testId={testId.append('requestButton')}
              disabled={activating}
            />
          </div>
        </StatusInfo>
      )}
    </>
  );
}
